/**
 * Shared family devices (trusted family devices, HP Phoenix 6.0.163+).
 *
 * A browser on a TV, tablet or kiosk pairs itself with a W- code that a
 * parent approves. It then holds a device credential and mints short-lived
 * member sessions when someone taps their avatar.
 *
 * Storage rules:
 * - The device credential lives in localStorage under `shared_device_token`
 *   on the family's own host. It is never put in `auth_token`, a query
 *   string or a log line.
 * - A member session is written to `auth_token` and `user` so every existing
 *   screen works unchanged, and is marked with `shared_device_session`. Any
 *   marked session is wiped on page load (see `wipeSharedSessionOnBoot`),
 *   on idle, on "Switch person", on pagehide and on a 401.
 *
 * Device-credential calls use `fetch` directly instead of the shared axios
 * client, whose interceptor would attach the member token and redirect to
 * /login on a 401.
 */

export const SHARED_DEVICE_IDLE_MS = 3 * 60 * 1000
/** Member sessions last 30 minutes; renew a little before that while in use. */
export const MEMBER_SESSION_RENEW_MS = 25 * 60 * 1000

const KEY_TOKEN = 'shared_device_token'
const KEY_NAME = 'shared_device_name'
const KEY_PAIRED_AT = 'shared_device_paired_at'
const KEY_SESSION = 'shared_device_session'
const KEY_LAST_FAMILY = 'hp_last_family_slug'

/**
 * Opens the step-up sheet. Detail: `{ method, path }` from the API client
 * (a 403 step_up_required), or `{ source: 'picker', parentId }` when a
 * parent avatar is tapped on the picker.
 */
export const STEP_UP_EVENT = 'hp:step-up-required'
/** Sent when a shared session ends and the screen should return to the picker. */
export const SHARED_EXIT_EVENT = 'hp:shared-device-exit'
/** Sent when the device credential stops working (revoked or trust expired). */
export const DEVICE_UNPAIRED_EVENT = 'hp:shared-device-unpaired'
/** How often a paired device checks that its credential still works. */
export const DEVICE_CHECK_MS = 60 * 1000
export const UNPAIRED_MESSAGE = 'This screen is no longer paired with a family. A parent can pair it again below.'

const BASE_DOMAIN = 'housepoints.ai'
// Hosts Phoenix does not treat as a family (FamilyDBManager.extract_family_slug).
const RESERVED_LABELS = ['api', 'www', 'app', 'admin', 'auth', 'staging', 'staging-phoenix', 'dev']

// ── Hosts ────────────────────────────────────────────────────────────────

export type HostKind =
  | { kind: 'family'; slug: string }
  | { kind: 'neutral' }
  | { kind: 'other' }

/**
 * `family` for `<slug>.housepoints.ai`, `neutral` for the apex and reserved
 * subdomains (app, staging, ...), `other` for localhost and anything else
 * (development: pair and store in place).
 */
export function hostKind(hostname: string = window.location.hostname): HostKind {
  const host = hostname.toLowerCase()
  if (host === BASE_DOMAIN) return { kind: 'neutral' }
  if (host.endsWith(`.${BASE_DOMAIN}`)) {
    const label = host.slice(0, -(BASE_DOMAIN.length + 1))
    if (!label || label.includes('.') || RESERVED_LABELS.includes(label)) return { kind: 'neutral' }
    return { kind: 'family', slug: label }
  }
  return { kind: 'other' }
}

export function familyOrigin(slug: string): string {
  return `https://${slug}.${BASE_DOMAIN}`
}

/** A family slug as a person might type it: "smith", "smith.housepoints.ai", a URL. */
export function normalizeFamilySlug(input: string): string | null {
  let s = input.trim().toLowerCase()
  s = s.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
  if (s.endsWith(`.${BASE_DOMAIN}`)) s = s.slice(0, -(BASE_DOMAIN.length + 1))
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(s)) return null
  if (RESERVED_LABELS.includes(s)) return null
  return s
}

export function rememberFamilySlug(slug: string) {
  try {
    localStorage.setItem(KEY_LAST_FAMILY, slug)
  } catch {}
}

export function lastFamilySlug(): string {
  try {
    return localStorage.getItem(KEY_LAST_FAMILY) || ''
  } catch {
    return ''
  }
}

// ── User agent ───────────────────────────────────────────────────────────

/** Default device name shown to the approving parent, who can rename it. */
export function deviceNameFromUserAgent(ua: string): string {
  if (/AFT[A-Z]|Fire TV|AmazonWebAppPlatform/i.test(ua)) return 'Fire TV'
  if (/Tizen|SamsungBrowser.*TV|SMART-TV.*Samsung/i.test(ua)) return 'Samsung TV'
  if (/Web0S|webOS|NetCast/i.test(ua)) return 'LG TV'
  if (/Android TV|GoogleTV|BRAVIA|Chromecast/i.test(ua)) return 'Android TV'
  if (/AppleTV|tvOS/i.test(ua)) return 'Apple TV'
  if (/SMART-TV|SmartTV|HbbTV|CrKey/i.test(ua)) return 'Smart TV'
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && /Mobile\//i.test(ua))) return 'iPad'
  if (/Kindle|Silk\//i.test(ua)) return 'Fire tablet'
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return 'Android tablet'
  const d = describeUserAgent(ua)
  return d.browser && d.os ? `${d.browser} on ${d.os}` : 'Web browser'
}

/** Browser and operating system for the approve page. Best effort. */
export function describeUserAgent(ua: string | null | undefined): { browser: string; os: string } {
  const s = ua || ''
  let browser = ''
  if (/SamsungBrowser/i.test(s)) browser = 'Samsung Internet'
  else if (/Silk\//i.test(s)) browser = 'Silk'
  else if (/Edg\//i.test(s)) browser = 'Edge'
  else if (/OPR\/|Opera/i.test(s)) browser = 'Opera'
  else if (/Firefox\//i.test(s)) browser = 'Firefox'
  else if (/Chrome\/|CriOS\//i.test(s)) browser = 'Chrome'
  else if (/Safari\//i.test(s)) browser = 'Safari'

  let os = ''
  if (/AFT[A-Z]|Fire TV/i.test(s)) os = 'Fire TV'
  else if (/Tizen/i.test(s)) os = 'Samsung Tizen'
  else if (/Web0S|webOS/i.test(s)) os = 'LG webOS'
  else if (/Android TV|GoogleTV/i.test(s)) os = 'Android TV'
  else if (/iPad/i.test(s)) os = 'iPadOS'
  else if (/iPhone|iPod/i.test(s)) os = 'iOS'
  else if (/Android/i.test(s)) os = 'Android'
  else if (/CrOS/i.test(s)) os = 'ChromeOS'
  else if (/Windows/i.test(s)) os = 'Windows'
  else if (/Macintosh|Mac OS X/i.test(s)) os = 'macOS'
  else if (/Linux/i.test(s)) os = 'Linux'

  return { browser, os }
}

// ── Codes ────────────────────────────────────────────────────────────────

const CODE_CHARS = 'A-Z2-9'

/** Normalizes a typed pairing code; keeps the prefix when present. */
export function normalizePairingCode(input: string): string {
  const s = input.trim().toUpperCase().replace(/\s+/g, '')
  const m = s.match(new RegExp(`^(K|TV|D|W)-?([${CODE_CHARS}]{6})$`))
  if (m) return `${m[1]}-${m[2]}`
  return s
}

/** Normalizes a typed step-up fallback code to `S-XXXXXX`, or null. */
export function normalizeStepUpCode(input: string): string | null {
  const s = input.trim().toUpperCase().replace(/\s/g, '')
  let rest = s.startsWith('S-') ? s.slice(2) : s.replace(/-/g, '')
  if (!s.startsWith('S-') && rest.length === 7 && rest.startsWith('S')) rest = rest.slice(1)
  return new RegExp(`^[${CODE_CHARS}]{6}$`).test(rest) ? `S-${rest}` : null
}

// ── Device credential ───────────────────────────────────────────────────

export function getDeviceCredential(): string | null {
  try {
    return localStorage.getItem(KEY_TOKEN)
  } catch {
    return null
  }
}

export function getDeviceName(): string {
  try {
    return localStorage.getItem(KEY_NAME) || 'Shared device'
  } catch {
    return 'Shared device'
  }
}

export function storeDeviceCredential(token: string, name: string) {
  localStorage.setItem(KEY_TOKEN, token)
  localStorage.setItem(KEY_NAME, name)
  localStorage.setItem(KEY_PAIRED_AT, new Date().toISOString())
}

export function clearDeviceCredential() {
  endSharedSession()
  try {
    localStorage.removeItem(KEY_TOKEN)
    localStorage.removeItem(KEY_NAME)
    localStorage.removeItem(KEY_PAIRED_AT)
  } catch {}
}

declare global {
  interface Window {
    __hpDevicePairHandoff?: string
  }
}

/**
 * Reads, once, the credential handed over in `/device#pair=...`. The inline
 * script in index.html captured the fragment and removed it from the
 * address bar and history before any other script ran.
 */
export function takePairingHandoff(): { token: string; name: string } | null {
  const raw = window.__hpDevicePairHandoff
  delete window.__hpDevicePairHandoff
  if (!raw) return null
  const params = new URLSearchParams(raw)
  const token = params.get('pair')
  if (!token || !/^[A-Za-z0-9_-]{16,256}$/.test(token)) return null
  return { token, name: (params.get('name') || 'Shared device').slice(0, 100) }
}

/** The URL a device on another host moves to after approval. */
export function handoffUrl(slug: string, token: string, name: string): string {
  const frag = new URLSearchParams({ pair: token, name }).toString()
  return `${familyOrigin(slug)}/device#${frag}`
}

// ── HTTP ─────────────────────────────────────────────────────────────────

export class HttpError extends Error {
  status: number
  data: any
  constructor(status: number, data: any) {
    super((data && (data.message || data.error)) || `Request failed (${status})`)
    this.status = status
    this.data = data
  }
}

async function request(
  path: string,
  opts: { method?: string; body?: unknown; token?: string | null } = {}
): Promise<any> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`
  const res = await fetch(path, {
    method: opts.method || 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'omit',
    cache: 'no-store',
  })
  let data: any = null
  try {
    data = await res.json()
  } catch {}
  if (!res.ok) throw new HttpError(res.status, data)
  return data
}

/** Anonymous pairing bootstrap routes. */
export const pairingApi = {
  requestCode(deviceName: string): Promise<{ code: string; expires_at: string; expires_in: number }> {
    return request('/api/platform/device/request-code', {
      method: 'POST',
      body: { device_type: 'web', device_name: deviceName },
    })
  },
  checkCode(code: string): Promise<{
    status: 'pending' | 'approved' | 'expired' | 'consumed' | 'revoked'
    token?: string
    family_slug?: string
  }> {
    return request(`/api/platform/device/check-code/${encodeURIComponent(code)}`)
  },
}

export interface DeviceMember {
  id: string
  display_name: string
  avatar_url?: string | null
  avatar_emoji?: string | null
  is_parent: boolean
}

interface SessionResponse {
  token: string
  expires_at: string
  expires_in: number
  parent_rights: boolean
  user: { id: string; display_name: string; avatar_url?: string | null; is_parent: boolean }
}

export type StepUpStatus =
  | { status: 'pending' }
  | { status: 'denied' }
  | { status: 'expired' }
  | { status: 'approved'; window: 'closed' | 'ended' | string }
  | ({ status: 'approved' } & SessionResponse)

function deviceRequest(path: string, opts: { method?: string; body?: unknown } = {}) {
  const token = getDeviceCredential()
  if (!token) return Promise.reject(new HttpError(401, { reason: 'missing_token' }))
  return request(path, { ...opts, token })
}

let memberCache: DeviceMember[] = []

export const deviceApi = {
  async members(): Promise<DeviceMember[]> {
    const data = await deviceRequest('/api/device/members')
    memberCache = data?.members || []
    return memberCache
  },
  cachedMembers(): DeviceMember[] {
    return memberCache
  },
  session(memberId: string): Promise<SessionResponse> {
    return deviceRequest('/api/device/session', { method: 'POST', body: { user_id: memberId } })
  },
  stepUp(parentId: string, action: string): Promise<{
    id: string
    fallback_code: string
    expires_at: string
    expires_in: number
    notified: number | boolean
  }> {
    return deviceRequest('/api/device/step-up', {
      method: 'POST',
      body: { parent_user_id: parentId, action },
    })
  },
  stepUpStatus(id: string): Promise<StepUpStatus> {
    return deviceRequest(`/api/device/step-up/${encodeURIComponent(id)}`)
  },
}

// ── Revocation check ────────────────────────────────────────────────────

let unpairedNotice: string | null = null

/**
 * The device credential was refused: forget it and any member session, and
 * tell the pairing screen why. DevicePage listens for DEVICE_UNPAIRED_EVENT;
 * a page mounted later reads the notice with takeUnpairedNotice().
 */
export function markDeviceUnpaired(message: string = UNPAIRED_MESSAGE) {
  clearDeviceCredential()
  unpairedNotice = message
  window.dispatchEvent(new CustomEvent(DEVICE_UNPAIRED_EVENT, { detail: { message } }))
}

/** The message left by markDeviceUnpaired, once. */
export function takeUnpairedNotice(): string | null {
  const n = unpairedNotice
  unpairedNotice = null
  return n
}

/**
 * Checks that this device's credential still works, using the member list
 * (the cheapest device-credential call; one small query). Revoking the
 * device or its trust expiring answers 401; a credential now pointing at a
 * display-scope or another family's pairing answers 403. Network errors and
 * 5xx leave the device paired.
 */
export async function checkDeviceCredential(): Promise<'ok' | 'unpaired' | 'unknown' | 'unpaired_already'> {
  if (!getDeviceCredential()) return 'unpaired_already'
  try {
    await deviceApi.members()
    return 'ok'
  } catch (err) {
    if (err instanceof HttpError && (err.status === 401 || err.status === 403)) {
      // A second check could have raced this one; only report it once.
      if (!getDeviceCredential()) return 'unpaired_already'
      markDeviceUnpaired()
      return 'unpaired'
    }
    return 'unknown'
  }
}

// ── Member session state ────────────────────────────────────────────────

export interface SharedSessionState {
  member: DeviceMember
  /** Epoch ms when the current token expires. */
  expiresAt: number
  /** Set while a step-up parent window is open. */
  parentWindowEndsAt: number | null
  startedAt: number
}

let state: SharedSessionState | null = null
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((l) => l())
}

export function subscribeSharedSession(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getSharedSession(): SharedSessionState | null {
  return state
}

/** True while `auth_token` holds a session minted by this shared device. */
export function isSharedSessionActive(): boolean {
  try {
    return localStorage.getItem(KEY_SESSION) === '1'
  } catch {
    return false
  }
}

function clearStoredSession() {
  try {
    if (localStorage.getItem(KEY_SESSION) === '1') {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
    }
    localStorage.removeItem(KEY_SESSION)
  } catch {}
}

async function fetchFullUser(token: string, member: DeviceMember): Promise<Record<string, any>> {
  let user: Record<string, any> = {}
  try {
    user = (await request('/api/auth/me', { token })) || {}
  } catch {}
  // Keep is_parent from the member list: /api/auth/me reports a shared-device
  // parent session without a step-up as a member.
  return {
    ...user,
    id: member.id,
    display_name: user.display_name || member.display_name,
    avatar_url: user.avatar_url || member.avatar_url || undefined,
    is_parent: member.is_parent,
    role: member.is_parent ? 'parent' : user.role || 'child',
  }
}

// Per-user caches (the React Query cache) are dropped whenever the person or
// the parent window changes, so a new session never shows the previous
// person's data or cached 403 results. SharedDeviceController registers it.
let resetHandler: (() => void) | null = null

export function registerSessionResetHandler(fn: (() => void) | null) {
  resetHandler = fn
}

function sessionKey(s: SharedSessionState | null): string {
  return s ? `${s.member.id}:${s.parentWindowEndsAt ? 'parent' : 'member'}` : ''
}

function resetUserCaches() {
  try {
    resetHandler?.()
  } catch {}
}

async function applySession(resp: SessionResponse, member: DeviceMember, parentWindow: boolean) {
  const user = await fetchFullUser(resp.token, member)
  // The credential check may have found the device revoked while this
  // session was being minted; do not sign anyone in on an unpaired screen.
  if (!getDeviceCredential()) throw new HttpError(401, { reason: 'device_unpaired' })
  localStorage.setItem(KEY_SESSION, '1')
  localStorage.setItem('auth_token', resp.token)
  localStorage.setItem('user', JSON.stringify(user))
  const expiresAt = Date.parse(resp.expires_at) || Date.now() + resp.expires_in * 1000
  const previousKey = sessionKey(state)
  state = {
    member,
    expiresAt,
    parentWindowEndsAt: parentWindow ? expiresAt : null,
    startedAt: Date.now(),
  }
  if (sessionKey(state) !== previousKey) resetUserCaches()
  emit()
}

/**
 * Mints a member session for `member` and makes it the app's session. Used
 * for kids; a parent on a shared device goes through step-up approval first
 * (a parent session without it has no parent rights, so the dashboard would
 * be empty).
 */
export async function startMemberSession(member: DeviceMember): Promise<void> {
  const resp = await deviceApi.session(member.id)
  await applySession(resp, { ...member, is_parent: resp.user?.is_parent ?? member.is_parent }, false)
}

/** Uses an approved step-up's parent session for its 10-minute window. */
export async function startParentWindow(resp: SessionResponse, member: DeviceMember): Promise<void> {
  await applySession(resp, { ...member, is_parent: true }, true)
}

/** Ends the member session (and any parent window). Keeps the device credential. */
export function endSharedSession() {
  clearStoredSession()
  if (state) {
    state = null
    resetUserCaches()
    emit()
  }
}

/**
 * Called before React renders. A member session never survives a reload:
 * wipe it, and send a paired device to its picker.
 */
export function wipeSharedSessionOnBoot() {
  try {
    clearStoredSession()
    const paired = !!localStorage.getItem(KEY_TOKEN)
    const path = window.location.pathname
    const appPath = path === '/' || path === '/login' || path.startsWith('/dashboard')
    if (paired && appPath && !localStorage.getItem('auth_token')) {
      window.history.replaceState(null, '', '/device')
    }
    window.addEventListener('pagehide', () => clearStoredSession())
  } catch {}
}

/**
 * Called by the API client on a 401 during a shared session (expired or
 * revoked session, or a parent window that ended): return to the picker.
 */
export function handleSharedSessionUnauthorized(_reason?: string) {
  endSharedSession()
  if (window.location.pathname !== '/device') {
    window.dispatchEvent(new CustomEvent(SHARED_EXIT_EVENT))
  }
}

/** The parent window ended: drop the parent session and return to the picker. */
export function endParentWindow() {
  endSharedSession()
  window.dispatchEvent(new CustomEvent(SHARED_EXIT_EVENT))
}

/** Short description of what a parent is approving, from the blocked request. */
export function describeAction(method: string, path: string): string {
  const p = path.toLowerCase()
  if (/verify|approve-completion|completions?\/.+\/approve/.test(p)) return 'Verify chores'
  if (/redemptions?|rewards?/.test(p)) return 'Manage rewards'
  if (/settings|features|subscription/.test(p)) return 'Change family settings'
  if (/deduction/.test(p)) return 'Deduct points'
  if (/users|members|kids/.test(p)) return 'Manage family members'
  if (/chores|assignments|rotation/.test(p)) return 'Manage chores'
  if (/points/.test(p)) return 'Adjust points'
  return method.toUpperCase() === 'GET' ? 'Open parent tools' : 'Parent action'
}

/** Message for a failed parent decision on a step-up request. */
export function stepUpDecisionError(err: any): string {
  const status = err?.response?.status || 0
  const data = err?.response?.data || {}
  if (status === 409) {
    const s = data.status
    if (s === 'expired') return 'This request has expired. Ask for approval again on the shared device.'
    if (s === 'approved') return 'This request was already approved.'
    if (s === 'denied') return 'This request was already declined.'
    return 'This request was already decided.'
  }
  if (status === 404) return 'No request matches this code. Check the code on the shared device; requests expire after 5 minutes.'
  if (status === 429) return 'Too many attempts. Wait a minute, then try again.'
  if (status === 403 && data.reason === 'shared_device_session') {
    return 'Approve from your own phone or computer, not from the shared device.'
  }
  if (status === 403) return 'Only a parent can approve this.'
  return 'Could not record your decision. Try again.'
}

/**
 * Parses a server timestamp. Some pairing columns are serialized without an
 * offset although they are UTC; treat those as UTC instead of local time.
 */
export function parseServerTime(value: string | null | undefined): Date | null {
  if (!value) return null
  const s = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}
